import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import RfidNav from "./RfidNav";
import {
  cardStyle,
  generateEpc,
  getAssociations,
  getRfidConfig,
  labelStyle,
  randomHex,
  useWarehouseProducts,
  useWarehouses,
} from "./rfidCommon";

// La lecture RFID est simulée par logiciel à partir des produits réels du dépôt
// source : on ne peut pas lire plus d'étiquettes que la quantité en stock.

const RfidTransfert = () => {
  const navigate = useNavigate();
  const config = useMemo(() => getRfidConfig(), []);
  const associations = useMemo(() => getAssociations(), []);
  const { warehouses, loading: loadingWarehouses } = useWarehouses();

  const [sourceId, setSourceId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const {
    products,
    total,
    loading: loadingProducts,
    error: productsError,
  } = useWarehouseProducts(sourceId);

  const [tags, setTags] = useState([]); // { epc, productId }
  const [reading, setReading] = useState(false);
  const [done, setDone] = useState(null);
  const intervalRef = useRef(null);
  const epcSetRef = useRef(new Set());
  const readCountRef = useRef({});
  const autoStopRef = useRef(null);

  const stopReading = () => {
    clearInterval(intervalRef.current);
    clearTimeout(autoStopRef.current);
    intervalRef.current = null;
    setReading(false);
  };

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(autoStopRef.current);
    },
    []
  );

  const resetReads = () => {
    stopReading();
    epcSetRef.current = new Set();
    readCountRef.current = {};
    setTags([]);
    setDone(null);
  };

  const onSourceChange = (e) => {
    resetReads();
    setSourceId(e.target.value);
  };

  const nextEpc = (product) => {
    const pool = associations
      .filter((a) => a.code === product.code)
      .map((a) => a.epc)
      .filter((epc) => !epcSetRef.current.has(epc));
    if (pool.length > 0) return pool[0];
    let epc = generateEpc(config.epcFormat);
    while (epcSetRef.current.has(epc)) epc = generateEpc(config.epcFormat);
    return epc;
  };

  // retourne le nombre d'étiquettes réellement ajoutées
  const addRandomTags = (count) => {
    const batch = [];
    for (let i = 0; i < count; i++) {
      const candidates = products.filter(
        (p) => (readCountRef.current[p.id] || 0) < p.quantity
      );
      if (candidates.length === 0) break;
      const remainingTotal = candidates.reduce(
        (sum, p) => sum + p.quantity - (readCountRef.current[p.id] || 0),
        0
      );
      let pick = Math.random() * remainingTotal;
      let product = candidates[candidates.length - 1];
      for (const p of candidates) {
        pick -= p.quantity - (readCountRef.current[p.id] || 0);
        if (pick <= 0) {
          product = p;
          break;
        }
      }
      const epc = nextEpc(product);
      epcSetRef.current.add(epc);
      readCountRef.current[product.id] =
        (readCountRef.current[product.id] || 0) + 1;
      batch.push({ epc, productId: product.id });
    }
    if (batch.length > 0) setTags((old) => [...old, ...batch]);
    return batch.length;
  };

  const startReading = () => {
    if (intervalRef.current) return;
    setReading(true);
    intervalRef.current = setInterval(() => {
      const added = addRandomTags(1 + Math.floor(Math.random() * 4));
      if (added === 0) stopReading();
    }, config.readIntervalMs);
    if (config.autoStopSeconds > 0) {
      autoStopRef.current = setTimeout(
        stopReading,
        config.autoStopSeconds * 1000
      );
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    tags.forEach((t) => {
      map[t.productId] = (map[t.productId] || 0) + 1;
    });
    return products
      .filter((p) => map[p.id])
      .map((p) => ({ ...p, qty: map[p.id] }));
  }, [tags, products]);

  const lastTags = tags.slice(-8).reverse();
  const sameWarehouse = sourceId !== "" && sourceId === destinationId;
  const stockTotal = products.reduce((sum, p) => sum + p.quantity, 0);
  const canRead = sourceId !== "" && products.length > 0;
  const canValidate =
    tags.length > 0 && destinationId !== "" && !sameWarehouse && !reading;
  const labelOf = (id) => {
    const w = warehouses.find((x) => String(x.id) === String(id));
    return w ? w.label : "";
  };

  const onValidate = () => {
    setDone({
      reference: "TR-RFID-" + randomHex(6),
      count: tags.length,
      products: grouped.length,
    });
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-broadcast-tower mr-2"></i>Transfert RFID
              </h4>
              <small className="text-muted">
                Lisez toutes les étiquettes d'un lot en une seule fois et
                transférez-les entre dépôts
              </small>
            </div>
            <button
              className="btn btn-outline-secondary mt-2 mt-sm-0"
              onClick={() => navigate("/transfertsByWarehouseManager")}
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Retour aux transferts
            </button>
          </div>
          <RfidNav />
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {done ? (
            <div className="card card-outline card-dark" style={cardStyle}>
              <div className="card-body text-center py-5">
                <i
                  className="fas fa-check-circle fa-3x mb-3 d-block"
                  style={{ color: "#28a745" }}
                ></i>
                <h5>Transfert RFID créé</h5>
                <p className="text-muted mb-1">
                  Référence <strong>{done.reference}</strong>
                </p>
                <p className="text-muted">
                  {done.count} étiquettes, {done.products} produits :{" "}
                  {labelOf(sourceId)} → {labelOf(destinationId)}
                </p>
                <button className="btn btn-dark" onClick={resetReads}>
                  <i className="fas fa-undo mr-1"></i>
                  Nouveau transfert
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="card card-outline card-dark" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-warehouse mr-2"></i>Dépôts
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>Dépôt source</label>
                        <select
                          className="form-control"
                          value={sourceId}
                          onChange={onSourceChange}
                          disabled={loadingWarehouses}
                        >
                          <option value="">
                            {loadingWarehouses ? "Chargement..." : "Choisir..."}
                          </option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.label}
                            </option>
                          ))}
                        </select>
                        {sourceId !== "" && (
                          <small className="text-muted">
                            {loadingProducts
                              ? "Chargement des produits du dépôt..."
                              : productsError
                              ? "Impossible de charger les produits du dépôt"
                              : `${products.length} produits en stock, ${stockTotal} articles${
                                  total > products.length
                                    ? ` (${products.length} sur ${total} produits chargés)`
                                    : ""
                                }`}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>Dépôt destination</label>
                        <select
                          className="form-control"
                          value={destinationId}
                          onChange={(e) => setDestinationId(e.target.value)}
                          disabled={loadingWarehouses}
                        >
                          <option value="">
                            {loadingWarehouses ? "Chargement..." : "Choisir..."}
                          </option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  {sameWarehouse && (
                    <small className="text-danger">
                      La source et la destination doivent être différentes
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4">
                  <div
                    className="card card-outline card-dark"
                    style={cardStyle}
                  >
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-satellite-dish mr-2"></i>Lecteur
                      </h3>
                    </div>
                    <div className="card-body text-center">
                      <div
                        className="d-flex align-items-center justify-content-center mx-auto mb-3 bg-dark text-white"
                        style={{
                          width: 96,
                          height: 96,
                          borderRadius: "50%",
                          boxShadow: reading
                            ? "0 0 0 8px rgba(40,167,69,.25)"
                            : "none",
                          transition: "box-shadow .3s",
                        }}
                      >
                        <i className="fas fa-wifi fa-2x"></i>
                      </div>
                      <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>
                        {tags.length}
                      </div>
                      <small className="text-muted d-block mb-3">
                        étiquettes lues
                      </small>
                      <span
                        className={`badge ${
                          reading ? "badge-success" : "badge-secondary"
                        } mb-1`}
                      >
                        {reading ? "Lecture en cours..." : "En attente"}
                      </span>
                      <small className="text-muted d-block mb-3">
                        Lecteur simulé
                      </small>
                      <div>
                        {reading ? (
                          <button
                            className="btn btn-danger btn-block"
                            onClick={stopReading}
                          >
                            <i className="fas fa-stop mr-1"></i>
                            Arrêter la lecture
                          </button>
                        ) : (
                          <button
                            className="btn btn-dark btn-block"
                            onClick={startReading}
                            disabled={!canRead}
                          >
                            <i className="fas fa-play mr-1"></i>
                            Démarrer la lecture
                          </button>
                        )}
                        <button
                          className="btn btn-outline-dark btn-block"
                          disabled={reading || !canRead}
                          onClick={() => addRandomTags(50)}
                        >
                          <i className="fas fa-pallet mr-1"></i>
                          Lire une palette (50)
                        </button>
                      </div>
                      {!canRead && (
                        <small className="text-muted d-block mt-2">
                          Choisissez un dépôt source qui contient des produits
                        </small>
                      )}
                    </div>
                  </div>

                  <div
                    className="card card-outline card-secondary"
                    style={cardStyle}
                  >
                    <div className="card-header">
                      <h3 className="card-title">Dernières lectures</h3>
                    </div>
                    <ul className="list-group list-group-flush">
                      {lastTags.length === 0 && (
                        <li className="list-group-item text-muted text-center">
                          Aucune lecture
                        </li>
                      )}
                      {lastTags.map((t) => (
                        <li
                          key={t.epc}
                          className="list-group-item py-2"
                          style={{ fontFamily: "monospace", fontSize: 12 }}
                        >
                          <i className="fas fa-tag mr-2 text-muted"></i>
                          {t.epc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="col-lg-8">
                  <div
                    className="card card-outline card-dark"
                    style={cardStyle}
                  >
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-boxes mr-2"></i>Produits lus
                        <span className="badge badge-dark ml-2">
                          {grouped.length}
                        </span>
                      </h3>
                    </div>
                    <div className="card-body p-0 table-responsive">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Code</th>
                            <th>Code à barre</th>
                            <th>Libellé</th>
                            <th className="text-center">Stock</th>
                            <th className="text-center">Lu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grouped.length === 0 && (
                            <tr>
                              <td
                                colSpan={5}
                                className="text-center text-muted py-4"
                              >
                                <i className="fas fa-tags fa-2x mb-2 d-block"></i>
                                Démarrez la lecture pour récupérer les
                                étiquettes
                              </td>
                            </tr>
                          )}
                          {grouped.map((p) => (
                            <tr key={p.id}>
                              <td>
                                <span className="badge badge-light border">
                                  {p.code}
                                </span>
                              </td>
                              <td>{p.barcode}</td>
                              <td>
                                <strong>{p.name}</strong>
                              </td>
                              <td className="text-center">{p.quantity}</td>
                              <td className="text-center">
                                <span className="badge badge-dark">
                                  {p.qty}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={resetReads}
                        disabled={tags.length === 0}
                      >
                        <i className="fas fa-undo mr-1"></i>
                        Réinitialiser
                      </button>
                      <button
                        className="btn btn-dark"
                        onClick={onValidate}
                        disabled={!canValidate}
                      >
                        <i className="fas fa-check mr-1"></i>
                        Valider le transfert
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default RfidTransfert;
